import { Fragment, Dispatch, SetStateAction, useMemo, useState } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { useTranslation } from 'react-i18next'

// local imports
import calendarr from 'assets/icons/calendar-gray.svg'
import time from 'assets/icons/time.svg'
import roomIcon from 'assets/icons/meeting-room-gray.svg'
import location from 'assets/icons/location-marker-gray.svg'
import building from 'assets/icons/bina-gray.svg'
import resIcon from 'assets/icons/meeting-res.svg'

import DateUtil from 'utils/DateUtil'
import useSaveMeetingRoomReservation from '../hooks/use-save-meeting-room-res'
import { IFilteredMeetingRoomList, IGetMeetingRoomTimeTableList, ISaveMeetingReservation } from 'types/MeetingRoomType'
import {
  ContentArea,
  Header,
  ImagePreview,
  InfoList,
  ReservationSummary,
  SidebarActionArea,
  SidebarCancelButton,
  SidebarConfirmButton,
  SidebarContainer,
} from '../../gym/gym-list/styled'
import { InfoListItem } from '../meeting/styled'
import MeetingRoomService from 'service/MeetingRoomService'
import FavoriteIconButton from 'components/ui/FavoriteIconButton'
import AuthUtil from 'utils/AuthUtil'

interface ScheduleSidebarProps {
  openModal: () => void
  closeAfterSave: () => void
  visible: boolean
  setVisible: Dispatch<SetStateAction<boolean>>
  roomInfo: IFilteredMeetingRoomList
  reservationInfos: IGetMeetingRoomTimeTableList[]
}

const sortByStartTime = <T extends { reservationStartHourStr: string }>(arr: T[]): T[] => {
  return arr.sort((a, b) => {
    const timeA = new Date(`1970-01-01T${a.reservationStartHourStr}`)
    const timeB = new Date(`1970-01-01T${b.reservationStartHourStr}`)
    return timeA.getTime() - timeB.getTime()
  })
}

export default function ScheduleSidebar({
  roomInfo,
  reservationInfos,
  visible,
  setVisible,
  openModal,
  closeAfterSave,
}: ScheduleSidebarProps) {
  const { t } = useTranslation()
  const date = DateUtil.dateFormat(new Date(reservationInfos[0]?.reservationEndHour))
  const timeList = sortByStartTime(reservationInfos)
  const reservationTime = useMemo(
    () =>
      timeList?.map((r) => (
        <Fragment key={r.meetingRoomId}>
          {r.reservationStartHourStr} - {r.reservationEndHourStr}
          <br />
        </Fragment>
      )),
    [timeList]
  )

  // Meeting Room information desctructuring
  const {
    id,
    isFavorite,
    floor: { blockName, cityName, countryName, districtName, locationName, name },
  } = roomInfo || {}
  const roomName = roomInfo.name
  const roomLocationName = `${locationName}, ${districtName}, ${cityName}, ${countryName}`
  const employee = AuthUtil.getEmployeeSummary()

  /* Handle Save Meeting Room personal work reservation | Custom Hook */
  const resInfos: ISaveMeetingReservation[] = reservationInfos.map((r) => ({
    MeetingRoomId: r?.meetingRoomId,
    reservationStartTime: r?.reservationStartHour,
    reservationEndTime: r?.reservationEndHour,
  }))
  const { loading, saveMeetingReservation } = useSaveMeetingRoomReservation(resInfos, closeAfterSave)

  const closeSidebar = () => {
    setVisible(false)
    openModal()
  }

  const reservationInfoItems = useMemo(
    () => [
      {
        id: 'room-name',
        icon: roomIcon,
        title: t('meeting-res-modal.room-name'),
        value: roomName,
      },
      {
        id: 'location-info',
        icon: location,
        title: t('lactation-res-modal.location'),
        value: roomLocationName,
      },
      {
        id: 'block-floor-info',
        icon: building,
        title: t('lactation-res-modal.block-floor'),
        value: `${blockName} / ${name}`,
      },
      {
        id: 'date-info',
        icon: calendarr,
        title: t('lactation-res-modal.reservation-date'),
        value: date,
      },
      {
        id: 'time-info',
        icon: time,
        title: t('lactation-res-modal.reservation-time'),
        value: reservationTime,
      },
    ],
    [blockName, name, reservationTime, date, roomLocationName, roomName, t]
  )

  /* Favorite Lactation Room */
  const [favorite, setFavorite] = useState(isFavorite)
  const handleAddFavorite = () => {
    MeetingRoomService.addMeetingRoomFavorite(id).then((r) => setFavorite(true))
  }
  const handleRemoveFavorite = () => {
    MeetingRoomService.removeMeetingRoomFavorite(id).then((r) => setFavorite(false))
  }

  return (
    <Sidebar
      style={{ width: '500px' }}
      className="of-bordered-sidebar-box"
      visible={visible}
      position="right"
      onHide={closeSidebar}
      dismissable={false}
      closeOnEscape={false}>
      <SidebarContainer>
        <ContentArea>
          <Header className="d-flex align-items-center gap-2">
            {t('meeting-res-modal.meeting-reservation')}
            {AuthUtil.isAdmin(employee?.authList) && (
              <FavoriteIconButton onSave={handleAddFavorite} onRemove={handleRemoveFavorite} isFavorite={favorite} />
            )}
          </Header>
          <ReservationSummary>{t('meeting-res-modal.reservation-summary')}</ReservationSummary>
          <ImagePreview url={resIcon} />

          <InfoList>
            <div className="reservation-subtitle">{t('meeting-res-modal.reservation-info')}</div>
            {reservationInfoItems.map((info) => (
              <InfoListItem key={info.id} hasList={info.id === 'time-info'}>
                {info.icon ? <img src={info.icon} alt={info.id} /> : <div style={{ width: 20, height: 20 }} />}
                <span className="reservation-info-title"> {info.title} : </span>
                <span className="reservation-info-value">{info.value}</span>
              </InfoListItem>
            ))}
          </InfoList>
        </ContentArea>

        {/* Action Buttons */}
        <SidebarActionArea>
          <SidebarCancelButton
            id="backToMeetingReservation"
            className="p-button-outlined"
            label={t('coiffeur-res-modal.back-to-modal')}
            onClick={closeSidebar}
          />

          <SidebarConfirmButton
            id="saveMeetingReservation"
            loading={loading}
            label={t('buttons.confirm')}
            onClick={saveMeetingReservation}
          />
        </SidebarActionArea>
      </SidebarContainer>
    </Sidebar>
  )
}
